import { z } from 'zod';

import { parsePhone } from '../phone/phone.ts';
import {
  fieldIssue,
  isIssueCode,
  type FieldIssue,
  type FieldIssueCode,
  type IssueParams,
} from './messages.ts';

export * from './messages.ts';

/** Turns one Zod issue into our code + parameters (both languages come from the catalog). */
function describe(
  issue: z.core.$ZodIssue,
  value: unknown,
): { code: FieldIssueCode; params?: IssueParams } {
  switch (issue.code) {
    case 'custom': {
      const code: unknown = issue.params?.dobCode;
      return isIssueCode(code) ? { code } : { code: 'invalid' };
    }
    case 'invalid_type':
      return value === undefined || value === null || value === ''
        ? { code: 'required' }
        : { code: issue.expected === 'int' ? 'not_integer' : 'invalid' };
    case 'too_small': {
      const min = Number(issue.minimum);
      if (issue.origin === 'number' || issue.origin === 'bigint') {
        return { code: 'number_too_small', params: { min } };
      }
      return min <= 1 && issue.origin === 'string'
        ? { code: 'required' }
        : { code: 'too_short', params: { min } };
    }
    case 'too_big': {
      const max = Number(issue.maximum);
      return issue.origin === 'number' || issue.origin === 'bigint'
        ? { code: 'number_too_large', params: { max } }
        : { code: 'too_long', params: { max } };
    }
    case 'invalid_format':
      if (issue.format === 'email') return { code: 'invalid_email' };
      if (issue.format === 'uuid') return { code: 'invalid_id' };
      return { code: 'invalid_format' };
    case 'invalid_value':
      return { code: 'invalid_choice' };
    case 'unrecognized_keys':
      return { code: 'unexpected_field' };
    default:
      return { code: 'invalid' };
  }
}

/** The submitted value at a path (Zod 4 does not keep the input in its issues). */
function valueAt(input: unknown, path: readonly PropertyKey[]): unknown {
  let current = input;
  for (const key of path) {
    if (typeof current !== 'object' || current === null) return undefined;
    current = (current as Record<PropertyKey, unknown>)[key];
  }
  return current;
}

/** Every issue of a failed parse, one per field, in both languages. Pass the submitted input. */
export function toFieldIssues(error: z.ZodError, input: unknown): FieldIssue[] {
  return error.issues.map((issue) => {
    const { code, params } = describe(issue, valueAt(input, issue.path));
    return fieldIssue(issue.path.map(String).join('.'), code, params);
  });
}

/**
 * A phone field for request schemas: accepts what people type, yields E.164, and refuses with the
 * exact rule (O17). Use it for every phone a person enters.
 */
export const phoneSchema = z.string().transform((value, ctx) => {
  const result = parsePhone(value);
  if (!result.ok) {
    ctx.addIssue({ code: 'custom', message: result.problem, params: { dobCode: result.problem } });
    return z.NEVER;
  }
  return result.e164;
});
